import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Typography } from "@material-ui/core";
import { DateField, GeneratePassword, NumberField, PriceField, SelectField, SubmitButton, TextField, UploadField } from "@microrealestate/commonui/components";
import { FieldArray, Form, Formik } from "formik";
import useTranslation from "next-translate/useTranslation";
import { useState, useEffect, useCallback, useContext, useMemo } from "react";
import useDialog from "../../hooks/useDialog";
import { StoreContext } from "../../store";
import { ADMIN_ROLE, ROLES, SUPERADMIN_ROLE, LANDLORD_ROLE } from "../../store/User";
import DeleteIcon from '@material-ui/icons/Delete';
import { useRouter } from "next/router";
import * as Yup from 'yup';
import { isArray, set } from "lodash";

function EditMemberDialog({ open, setOpen, memberData }) {
  const { t } = useTranslation('common');
  const store = useContext(StoreContext);
  const router = useRouter();
  const [user, setUser] = useState({});
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [dataMember, setDataMember] = useState([]);
  const [roleValues, setRoleValues] = useState([]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        if (memberData.role === "super admin") {
          memberData.scopeProperty = [];
        }

        const data = store.organization.selected.members
        const filteredData = data.filter(item => item.email !== memberData.email);
        setDataMember(filteredData)
      } catch (error) {
        console.error('Error fetching properties:', error);
      }
    };
    
    fetchProperties();
  }, [memberData, memberData.email, store.organization.selected.members, store.property]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const onSubmit = useCallback(async (dataUser) => {
    try {
      let transformedData;

      if (dataUser.role === LANDLORD_ROLE || dataUser.role === SUPERADMIN_ROLE) {
        transformedData = {
          ...dataUser,
          scopeProperty: ['fullAccess']
        };
      } else {
        if (dataUser.scopeProperty.length === 0) {
          store.pushToastMessage({
            message: t('Please select at least one property'),
            severity: 'error',
          });
          return;
        }

        transformedData = {
          ...dataUser,
          scopeProperty: dataUser.scopeProperty.filter(prop => prop && prop !== '') // Remove empty strings
        };
      }

      const response = await store.user.updateUser(
        memberData._id,
        transformedData.email,
        transformedData.fullName,
        transformedData.role,
        transformedData.isEnabled,
        transformedData.scopeProperty
      );
  
      if (response.status !== 200) {
        switch (response.status) {
          default:
            store.pushToastMessage({
              message: t('Something went wrong'),
              severity: 'error',
            });
            break;
        }
      } 
    } catch (error) {
      console.error('Error updating user:', error);
      store.pushToastMessage({
        message: t('Error updating user'),
        severity: 'error',
      });
    }

    const newUpdatedMembers = await store.organization.selected.members.map(existingMember => {
      if (existingMember.userId === memberData._id) {
        return {
          userId: memberData._id,
          email: dataUser.email,
          name: dataUser.fullName,
          role: dataUser.role,
        };
      } else {
        return existingMember;
      }
    });

    const organization = {
      ...store.organization.selected,
      members : newUpdatedMembers,
    };

    const { status, data: updatedOrganization } =
      await store.organization.update(organization);
    if (status !== 200) {
      switch (status) {
        case 422:
          return store.pushToastMessage({
            message: t('Some fields are missing'),
            severity: 'error',
          });
        case 403:
          return store.pushToastMessage({
            message: t('You are not allowed to update the settings'),
            severity: 'error',
          });
        case 409:
          return store.pushToastMessage({
            message: t('The organization name already exists'),
            severity: 'error',
          });
        default:
          return store.pushToastMessage({
            message: t('Something went wrong'),
            severity: 'error',
          });
      }
    } else {
      store.organization.setSelected(updatedOrganization, store.user);
      store.organization.setItems([
        ...store.organization.items.filter(
          ({ _id }) => _id !== updatedOrganization._id
        ),
        updatedOrganization,
      ]);
      store.pushToastMessage({
        message: t("Success edit member's data"),
        severity: 'success',
      });
      
    }

    handleClose();
    await router.push(
      `/settings`
    );
  }, [handleClose, memberData._id, router, store, t]);  

  useEffect(() => {
    async function fetchRoles() {
      let roles = await store.rbac?.getRoles?.() || [];
      roles = roles.filter(role => role.key !== LANDLORD_ROLE);
      setRoleValues(
        roles.map((role) => ({
          id: role._id,
          label: t(role.name),
          value: role.key,
        }))
      );
    }
    fetchRoles();
  }, [store.rbac, t]);

  const availableProperties = useMemo(
    () => store.property.items.map((property) => ({ id: property._id, label: property.name, value: property._id })),
    [store.property.items]
  );

  const handleRemoveProperty = useCallback((index) => {
    const newSelectedProperties = [...selectedProperties];
    newSelectedProperties.splice(index, 1);
    setSelectedProperties(newSelectedProperties);
  }, [selectedProperties]);

  const initialValues = {
    fullName: `${memberData.fullName}`,
    email: `${memberData.email}`,
    role: `${memberData.role}`,
    scopeProperty: memberData.scopeProperty.length ? 
    memberData.scopeProperty.map(({ id }) => id) : [''],
  }

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        fullName: Yup.string().required(),
        email: Yup.string()
          .email()
          .notOneOf(dataMember.map(({ email }) => email), t("Email has been used"))
          .required(),
        role: Yup.string().required(t("Role is a required field")),
        scopeProperty: Yup.array().when('role', {
          is: (role) => role !== LANDLORD_ROLE && role !== SUPERADMIN_ROLE,
          then: Yup.array().of(
            Yup.string().required(t("Property is a required field"))
          ).min(1, t("At least one property must be selected")),
          otherwise: Yup.array()
        })
      }),
    [dataMember, t]
  );

  return user && (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={handleClose}
      aia-labelledby="edit-services-dialog"
    >
      <Box p={1}>
        <Formik 
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit= {onSubmit} 
        >
          {({ isSubmitting, values, setFieldValue}) => (
            <Form autoComplete="off">
              <DialogTitle id="form-dialog-title">
                {t('Edit team member')}
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  {t('Edit a new team member to your organization')}
                </DialogContentText>
                <Box minHeight={100} minWidth={500}>
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <TextField label={t('Name')} name="fullName" />
                    </Grid>
                    <Grid item xs={8}>
                      <TextField label={t('Email')} name="email" />
                    </Grid>
                    <Grid item xs={4}>
                      <SelectField
                        label={t('Role')}
                        name="role"
                        values={roleValues}
                        onChange={(e) => {
                          // console.log('values.role', values.role);
                          
                          // values.scopeProperty = [];
                          setFieldValue('scopeProperty', ['']);
                          setSelectedProperties([]);
                          setFieldValue('role', e.target.value);
                        }}
                      />
                    </Grid>
                    {values.role !== SUPERADMIN_ROLE && values.role !== LANDLORD_ROLE ? (
                      <FieldArray name="scopeProperty">
                        {({ push, remove }) => (
                          <>
                            {isArray(values.scopeProperty) && values.scopeProperty.map((property, index) => (
                              <Grid container spacing={2} key={index}>
                                <Grid item xs={8}>
                                  <SelectField
                                    label={t('Property')}
                                    name={`scopeProperty.${index}`}
                                    values={availableProperties}
                                  />
                                </Grid>
                                {index !== 0 && (
                                  <Grid item xs={4}>
                                    <Button onClick={() => {
                                      remove(index);
                                      handleRemoveProperty(index);
                                    }}>
                                      <DeleteIcon />
                                    </Button>
                                  </Grid>
                                )}
                                
                              </Grid>
                            ))}
                            {values.scopeProperty?.length < availableProperties.length && ( 
                              <Grid item xs={12}>
                                <Button 
                                  onClick={() => {
                                    push('');
                                  }}
                                >
                                  {t('Add property')}
                                </Button>
                              </Grid>
                            )}
                          </>
                        )}
                      </FieldArray>
                    ) : (
                      <Grid item xs={12}>
                        <Typography variant="caption" style={{color:"rgba(0, 0, 0, 0.54)"}}>{t('Property')}</Typography>
                        <Typography>{t('All property')}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose}>{t('Cancel')}</Button>
                <SubmitButton
                  label={t("Edit")}
                />
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Box>
    </Dialog>
  )
}

export default function useEditMemberDialog() {
  return useDialog(EditMemberDialog);
}